import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Select as ChakraSelect,
    Checkbox,
    Divider,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Heading,
    IconButton,
    Image,
    Input,
    SimpleGrid,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { createPatientFormData, getAllFacilitiesOutside, validatePatientId } from '../../../api';
import TitleHeader from '../../../components/TitleHeader';

const STATES = [
    { label: 'Alabama', value: 'AL' }, { label: 'Alaska', value: 'AK' }, { label: 'Arizona', value: 'AZ' },
    { label: 'Arkansas', value: 'AR' }, { label: 'California', value: 'CA' }, { label: 'Colorado', value: 'CO' },
    { label: 'Connecticut', value: 'CT' }, { label: 'Delaware', value: 'DE' }, { label: 'Florida', value: 'FL' },
    { label: 'Georgia', value: 'GA' }, { label: 'Hawaii', value: 'HI' }, { label: 'Idaho', value: 'ID' },
    { label: 'Illinois', value: 'IL' }, { label: 'Indiana', value: 'IN' }, { label: 'Iowa', value: 'IA' },
    { label: 'Kansas', value: 'KS' }, { label: 'Kentucky', value: 'KY' }, { label: 'Louisiana', value: 'LA' },
    { label: 'Maine', value: 'ME' }, { label: 'Maryland', value: 'MD' }, { label: 'Massachusetts', value: 'MA' },
    { label: 'Michigan', value: 'MI' }, { label: 'Minnesota', value: 'MN' }, { label: 'Mississippi', value: 'MS' },
    { label: 'Missouri', value: 'MO' }, { label: 'Montana', value: 'MT' }, { label: 'Nebraska', value: 'NE' },
    { label: 'Nevada', value: 'NV' }, { label: 'New Hampshire', value: 'NH' }, { label: 'New Jersey', value: 'NJ' },
    { label: 'New Mexico', value: 'NM' }, { label: 'New York', value: 'NY' }, { label: 'North Carolina', value: 'NC' },
    { label: 'North Dakota', value: 'ND' }, { label: 'Ohio', value: 'OH' }, { label: 'Oklahoma', value: 'OK' },
    { label: 'Oregon', value: 'OR' }, { label: 'Pennsylvania', value: 'PA' }, { label: 'Rhode Island', value: 'RI' },
    { label: 'South Carolina', value: 'SC' }, { label: 'South Dakota', value: 'SD' }, { label: 'Tennessee', value: 'TN' },
    { label: 'Texas', value: 'TX' }, { label: 'Utah', value: 'UT' }, { label: 'Vermont', value: 'VT' },
    { label: 'Virginia', value: 'VA' }, { label: 'Washington', value: 'WA' }, { label: 'West Virginia', value: 'WV' },
    { label: 'Wisconsin', value: 'WI' }, { label: 'Wyoming', value: 'WY' }
];

const isValidEmail = (e) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());

const digitsOnly = (s) => (s || '').replace(/\D+/g, '');

const calcAge = (dobStr) => {
    if (!dobStr) return '';
    const dob = new Date(dobStr);
    if (isNaN(dob)) return '';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age >= 0 ? String(age) : '';
};

const isPastDate = (dStr) => {
    if (!dStr) return false;
    const d = new Date(dStr);
    const today = new Date();
    d.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return d < today;
};

const isFutureDate = (dStr) => {
    if (!dStr) return false;
    const d = new Date(dStr);
    const today = new Date();
    d.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return d > today;
};

const PatientCreatePage = () => {
    const toast = useToast();
    const navigate = useNavigate();

    // Step state
    const [step, setStep] = useState(1);
    const next = () => setStep((s) => Math.min(3, s + 1));
    const back = () => setStep((s) => Math.max(1, s - 1));

    // Facility (top)
    const [facilities, setFacilities] = useState([]);
    const [facilitySelect, setFacilitySelect] = useState(null);

    // Step 1: Identity & Contact
    const [identity, setIdentity] = useState({
        patientNumber: '',
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
        sex: '',
        phone: '',
        email: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: ''
    });
    const [addrStateSelect, setAddrStateSelect] = useState(null);
    const age = useMemo(() => calcAge(identity.dob), [identity.dob]);

    // Step 2: Gov ID + Insurances[]
    const [gov, setGov] = useState({
        idNumber: '',
        issuingState: '',
        expiration: '',
        frontFile: null,
        backFile: null
    });
    const [govStateSelect, setGovStateSelect] = useState(null);
    const [sendGovLink, setSendGovLink] = useState(false);

    const [insurances, setInsurances] = useState([
        {
            payerName: '',
            planName: '',
            memberId: '',
            groupNo: '',
            subscriber: '',
            relationship: '',
            effectiveAt: '',
            frontFile: null,
            backFile: null
        }
    ]);
    const [sendInsLink, setSendInsLink] = useState(false);

    // Previews
    const [idFrontPreview, setIdFrontPreview] = useState(null);
    const [idBackPreview, setIdBackPreview] = useState(null);
    const [insPreviews, setInsPreviews] = useState([{ front: null, back: null }]);

    // Refs to revoke on unmount
    const revokeQueue = useRef([]);

    // Real-time duplicate check state
    const [idCheck, setIdCheck] = useState({
        loading: false,
        exists: false,
        count: 0,
        details: null,
        message: ''
    });
    const checkTimer = useRef(null);
    const lastQueryRef = useRef('');

    useEffect(() => {
        (async () => {
            const res = await getAllFacilitiesOutside();
            if (res?.success) setFacilities(res.data.facilities || []);
        })();
    }, []);

    // Preview effects
    useEffect(() => {
        if (gov.frontFile) {
            const url = URL.createObjectURL(gov.frontFile);
            setIdFrontPreview((prev) => {
                if (prev) revokeQueue.current.push(prev);
                return url;
            });
        }
    }, [gov.frontFile]);

    useEffect(() => {
        if (gov.backFile) {
            const url = URL.createObjectURL(gov.backFile);
            setIdBackPreview((prev) => {
                if (prev) revokeQueue.current.push(prev);
                return url;
            });
        }
    }, [gov.backFile]);

    useEffect(() => {
        setInsPreviews((prev) => {
            const nextArr = [...prev];
            while (nextArr.length < insurances.length) nextArr.push({ front: null, back: null });
            while (nextArr.length > insurances.length) nextArr.pop();

            insurances.forEach((ins, idx) => {
                if (ins.frontFile) {
                    const url = URL.createObjectURL(ins.frontFile);
                    if (nextArr[idx]?.front) revokeQueue.current.push(nextArr[idx].front);
                    nextArr[idx] = { ...(nextArr[idx] || {}), front: url };
                }
                if (ins.backFile) {
                    const url = URL.createObjectURL(ins.backFile);
                    if (nextArr[idx]?.back) revokeQueue.current.push(nextArr[idx].back);
                    nextArr[idx] = { ...(nextArr[idx] || {}), back: url };
                }
            });
            return nextArr;
        });
    }, [insurances]);

    // Revoke blob URLs on unmount
    useEffect(() => {
        return () => {
            revokeQueue.current.forEach((u) => {
                try { URL.revokeObjectURL(u); } catch { }
            });
            if (idFrontPreview) try { URL.revokeObjectURL(idFrontPreview); } catch { }
            if (idBackPreview) try { URL.revokeObjectURL(idBackPreview); } catch { }
            insPreviews.forEach((p) => {
                if (p?.front) try { URL.revokeObjectURL(p.front); } catch { }
                if (p?.back) try { URL.revokeObjectURL(p.back); } catch { }
            });
        };
    }, []); // eslint-disable-line

    const req = (v) => v && String(v).trim() !== '';
    const toastErr = (msg) => { toast({ title: 'Validation', description: msg, status: 'error', duration: 2200, isClosable: true }); return false; };

    // VALIDATION STEP 1
    const validateStep1 = () => {
        if (!facilitySelect?.value) return toastErr('Facility is required');

        const { firstName, lastName, dob, sex, phone, email, addressLine1, city, state, zip } = identity;

        if (!req(firstName)) return toastErr('First Name is required');
        if (!req(lastName)) return toastErr('Last Name is required');
        if (!req(dob)) return toastErr('DOB is required');
        if (!req(sex)) return toastErr('Sex is required');

        const phoneDigits = digitsOnly(phone);
        if (!req(phoneDigits)) return toastErr('Phone is required');
        if (phoneDigits.length < 10 || phoneDigits.length > 15) return toastErr('Phone must be 10–15 digits');

        if (!req(email)) return toastErr('Email is required');
        if (!isValidEmail(email)) return toastErr('Invalid email');

        if (!req(addressLine1)) return toastErr('Address Line 1 is required');
        if (!req(city)) return toastErr('City is required');
        if (!req(state)) return toastErr('State is required');
        if (!req(zip)) return toastErr('ZIP is required');

        return true;
    };

    // VALIDATION STEP 2 (text inputs ALWAYS required; uploads only if not sending link)
    const validateStep2 = () => {
        // GOV text inputs always
        if (!req(gov.idNumber)) return toastErr('Government ID Number is required');
        if (!req(gov.issuingState)) return toastErr('Government ID Issuing State is required');
        if (!req(gov.expiration)) return toastErr('Government ID Expiration is required');
        if (isPastDate(gov.expiration)) return toastErr('Government ID is expired');
        if (idCheck.exists) return toastErr('Duplicate ID number in this facility');

        // GOV uploads only if NOT sending link
        if (!sendGovLink) {
            if (!gov.frontFile) return toastErr('Government ID Front image is required');
            if (!gov.backFile) return toastErr('Government ID Back image is required');
        }

        // INSURANCE text inputs always
        if (insurances.length === 0) return toastErr('At least one insurance is required');
        for (let idx = 0; idx < insurances.length; idx++) {
            const ins = insurances[idx];
            if (!req(ins.payerName)) return toastErr(`Insurance #${idx + 1}: Payer Name is required`);
            if (!req(ins.planName)) return toastErr(`Insurance #${idx + 1}: Plan Name is required`);
            if (!req(ins.memberId)) return toastErr(`Insurance #${idx + 1}: Member ID is required`);
            if (!req(ins.groupNo)) return toastErr(`Insurance #${idx + 1}: Group No is required`);
            if (!req(ins.subscriber)) return toastErr(`Insurance #${idx + 1}: Subscriber is required`);
            if (!req(ins.relationship)) return toastErr(`Insurance #${idx + 1}: Relationship is required`);
            if (!req(ins.effectiveAt)) return toastErr(`Insurance #${idx + 1}: Effective At is required`);
            if (isFutureDate(ins.effectiveAt)) return toastErr(`Insurance #${idx + 1}: Effective date must be active (not in the future)`);

            // uploads only if NOT sending link
            if (!sendInsLink) {
                if (!ins.frontFile) return toastErr(`Insurance #${idx + 1}: Card Front image is required`);
                if (!ins.backFile) return toastErr(`Insurance #${idx + 1}: Card Back image is required`);
            }
        }

        return true;
    };

    // ===== Real-time duplicate check (debounced) =====
    useEffect(() => {
        const facilityId = facilitySelect?.value;
        const idNumber = (gov.idNumber || '').trim();
        const issuingState = (gov.issuingState || '').trim();

        if (checkTimer.current) clearTimeout(checkTimer.current);

        // Need facility + id + state to check
        if (!facilityId || !idNumber || !issuingState) {
            setIdCheck({ loading: false, exists: false, count: 0, details: null, message: '' });
            return;
        }

        const key = `${facilityId}|${idNumber}|${issuingState}`;
        lastQueryRef.current = key;
        setIdCheck((s) => ({ ...s, loading: true }));

        checkTimer.current = setTimeout(async () => {
            try {
                const res = await validatePatientId({
                    idNumber,
                    issuingState,
                    facilityId,
                });

                if (lastQueryRef.current !== key) return; // stale

                if (res?.success) {
                    const exists = !!res.data?.duplicate;
                    setIdCheck({
                        loading: false,
                        exists,
                        count: res.data?.count || (exists ? 1 : 0),
                        details: res.data?.match || null,
                        message: exists ? 'A patient with this ID already exists in this facility.' : ''
                    });
                } else {
                    setIdCheck({ loading: false, exists: false, count: 0, details: null, message: '' });
                }
            } catch {
                setIdCheck({ loading: false, exists: false, count: 0, details: null, message: '' });
            }
        }, 400);

        return () => {
            if (checkTimer.current) clearTimeout(checkTimer.current);
        };
    }, [facilitySelect?.value, gov.idNumber, gov.issuingState]);

    // INSURANCE ADD/REMOVE
    const addInsurance = () => {
        setInsurances((prev) => [...prev, {
            payerName: '', planName: '', memberId: '', groupNo: '',
            subscriber: '', relationship: '', effectiveAt: '',
            frontFile: null, backFile: null
        }]);
        setInsPreviews((prev) => [...prev, { front: null, back: null }]);
    };
    const removeInsurance = (idx) => {
        setInsurances((prev) => prev.filter((_, i) => i !== idx));
        setInsPreviews((prev) => prev.filter((_, i) => i !== idx));
    };

    // SUBMIT
    const [submitting, setSubmitting] = useState(false);
    const submit = async () => {
        if (!validateStep1() || !validateStep2()) return;

        // Final server-side duplicate recheck just before submit
        try {
            const finalCheck = await validatePatientId({
                idNumber: (gov.idNumber || '').trim(),
                issuingState: (gov.issuingState || '').trim(),
                facilityId: facilitySelect?.value
            });
            if (finalCheck?.success && finalCheck.data?.duplicate) {
                toastErr('Duplicate ID number in this facility');
                return;
            }
        } catch {
            // ignore and proceed
        }

        setSubmitting(true);
        try {
            const fd = new FormData();

            // facility
            fd.append('facility', facilitySelect.value);

            // identity/contact fields
            const phoneDigits = digitsOnly(identity.phone);
            Object.entries({
                patientNumber: identity.patientNumber,  // optional
                firstName: identity.firstName,
                middleName: identity.middleName,        // optional
                lastName: identity.lastName,
                dob: identity.dob,
                sex: identity.sex,
                phone: phoneDigits,
                email: identity.email,
                addressLine1: identity.addressLine1,
                addressLine2: identity.addressLine2,    // optional
                city: identity.city,
                state: identity.state,
                zip: identity.zip
            }).forEach(([k, v]) => {
                if (v !== undefined && v !== null && String(v).trim() !== '') fd.append(k, v);
            });

            // ---- GOVERNMENT ID: always send text; include multiple key shapes ----
            const idNum = (gov.idNumber || '').trim();
            const issState = (gov.issuingState || '').trim();
            const exp = (gov.expiration || '').trim();

            fd.append('govIdNumber', idNum);
            fd.append('govIssuingState', issState);
            fd.append('govExpiration', exp);

            fd.append('governmentId[idNumber]', idNum);
            fd.append('governmentId[issuingState]', issState);
            fd.append('governmentId[expiration]', exp);

            fd.append('governmentId.idNumber', idNum);
            fd.append('governmentId.issuingState', issState);
            fd.append('governmentId.expiration', exp);

            if (sendGovLink) fd.append('sendGovLink', 'true');

            // documents[] + docTypes (order matters)
            const docTypes = [];

            // GOV uploads only if not sending link
            if (!sendGovLink) {
                if (gov.frontFile) { fd.append('documents', gov.frontFile); docTypes.push('idFront'); }
                if (gov.backFile) { fd.append('documents', gov.backFile); docTypes.push('idBack'); }
            }

            // ---- INSURANCES: always send text payload; images only if not link ----
            const insPayload = insurances.map((i) => ({
                payerName: i.payerName,
                planName: i.planName,
                memberId: i.memberId,
                groupNo: i.groupNo,
                subscriber: i.subscriber,
                relationship: i.relationship,
                effectiveAt: i.effectiveAt
            }));
            const insJson = JSON.stringify(insPayload);

            fd.append('insurances', insJson);
            fd.append('insurancesJson', insJson);

            if (sendInsLink) fd.append('sendInsLink', 'true');

            if (!sendInsLink) {
                insurances.forEach((i) => {
                    if (i.frontFile) { fd.append('documents', i.frontFile); docTypes.push('insFront'); }
                    if (i.backFile) { fd.append('documents', i.backFile); docTypes.push('insBack'); }
                });
            }

            if (docTypes.length) {
                fd.append('docTypes', docTypes.join(','));
                docTypes.forEach((t) => fd.append('docTypes[]', t));
            }

            const res = await createPatientFormData(fd);
            if (!res?.success) throw new Error(res?.error || 'Create failed');

            toast({ title: 'Patient created', status: 'success', duration: 2000, isClosable: true });
            navigate('/reqs/patients');
        } catch (e) {
            toast({ title: 'Error', description: e.message || 'Failed to create', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setSubmitting(false);
        }
    };

    // Block next if duplicate (always), or while checking
    const blockNextOnDup = idCheck.exists;

    return (
        <Box maxW="6xl" mx="auto" p={8}>
            <TitleHeader title="Create Patient" />

            {/* Facility at top */}
            <VStack align="stretch" mt={4} spacing={4}>
                <FormControl isRequired>
                    <FormLabel>Facility</FormLabel>
                    <Select
                        options={facilities.map((f) => ({ value: f._id, label: `${f.name} - ${f.address?.city || 'N/A'}` }))}
                        value={facilitySelect}
                        onChange={(opt) => setFacilitySelect(opt)}
                        placeholder="Select facility"
                        isClearable
                    />
                </FormControl>
            </VStack>

            {/* Step indicator */}
            <HStack mt={6} mb={2} spacing={3}>
                <Badge colorScheme={step === 1 ? 'teal' : 'gray'}>Step 1: Identity & Contact</Badge>
                <Badge colorScheme={step === 2 ? 'teal' : 'gray'}>Step 2: Gov ID & Insurance</Badge>
                <Badge colorScheme={step === 3 ? 'teal' : 'gray'}>Step 3: Review</Badge>
            </HStack>
            <Divider mb={6} />

            {/* STEP 1 */}
            {step === 1 && (
                <VStack spacing={6} align="stretch">
                    <Heading size="md">Identity & Contact</Heading>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                        {/* <FormControl>
              <FormLabel>Patient # (optional)</FormLabel>
              <Input value={identity.patientNumber} onChange={(e) => setIdentity((s) => ({ ...s, patientNumber: e.target.value }))} />
            </FormControl> */}
                        <FormControl isRequired>
                            <FormLabel>First Name</FormLabel>
                            <Input value={identity.firstName} onChange={(e) => setIdentity((s) => ({ ...s, firstName: e.target.value }))} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Middle Name or M.I (optional)</FormLabel>
                            <Input value={identity.middleName} onChange={(e) => setIdentity((s) => ({ ...s, middleName: e.target.value }))} />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Last Name</FormLabel>
                            <Input value={identity.lastName} onChange={(e) => setIdentity((s) => ({ ...s, lastName: e.target.value }))} />
                        </FormControl>

                        {/* DOB + inline Age */}
                        <FormControl isRequired>
                            <FormLabel>Date of Birth</FormLabel>
                            <HStack>
                                <Input type="date" value={identity.dob} onChange={(e) => setIdentity((s) => ({ ...s, dob: e.target.value }))} />
                                <Text minW="60px" textAlign="right"><b>Age:</b> {age || '—'}</Text>
                            </HStack>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Sex</FormLabel>
                            <ChakraSelect value={identity.sex} onChange={(e) => setIdentity((s) => ({ ...s, sex: e.target.value }))}>
                                <option value="">Select</option>
                                <option value="M">Male</option>
                                <option value="F">Female</option>
                                <option value="X">Non-binary/Unspecified</option>
                                <option value="U">Unknown</option>
                            </ChakraSelect>
                        </FormControl>

                        {/* Phone digits-only */}
                        <FormControl isRequired>
                            <FormLabel>Phone</FormLabel>
                            <Input
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={identity.phone}
                                onChange={(e) => {
                                    const val = digitsOnly(e.target.value).slice(0, 15);
                                    setIdentity((s) => ({ ...s, phone: val }));
                                }}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Email</FormLabel>
                            <Input
                                type="email"
                                value={identity.email}
                                onChange={(e) => setIdentity((s) => ({ ...s, email: e.target.value }))}
                                onBlur={() => {
                                    if (identity.email && !isValidEmail(identity.email)) {
                                        toast({ title: 'Invalid email', status: 'warning', duration: 2000, isClosable: true });
                                    }
                                }}
                            />
                        </FormControl>

                    </SimpleGrid>

                    <Heading size="sm" mt={4}>Contact</Heading>
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
                        <FormControl isRequired><FormLabel>Address Line 1</FormLabel><Input value={identity.addressLine1} onChange={(e) => setIdentity((s) => ({ ...s, addressLine1: e.target.value }))} /></FormControl>
                        <FormControl><FormLabel>Address Line 2 (optional)</FormLabel><Input value={identity.addressLine2} onChange={(e) => setIdentity((s) => ({ ...s, addressLine2: e.target.value }))} /></FormControl>
                        <FormControl isRequired><FormLabel>City</FormLabel><Input value={identity.city} onChange={(e) => setIdentity((s) => ({ ...s, city: e.target.value }))} /></FormControl>
                        <FormControl isRequired>
                            <FormLabel>State</FormLabel>
                            <Select
                                options={STATES}
                                value={addrStateSelect}
                                onChange={(opt) => {
                                    setAddrStateSelect(opt);
                                    setIdentity((s) => ({ ...s, state: opt ? opt.value : '' }));
                                }}
                                placeholder="Select state"
                                isClearable
                            />
                        </FormControl>
                        <FormControl isRequired><FormLabel>ZIP</FormLabel><Input value={identity.zip} onChange={(e) => setIdentity((s) => ({ ...s, zip: e.target.value }))} /></FormControl>
                    </SimpleGrid>

                    <HStack justify="space-between" mt={2}>
                        <Box />
                        <Button colorScheme="teal" onClick={() => validateStep1() && next()}>Next</Button>
                    </HStack>
                </VStack>
            )}

            {/* STEP 2 */}
            {step === 2 && (
                <VStack spacing={6} align="stretch">
                    <Heading size="md">Government ID</Heading>

                    <Checkbox isChecked={sendGovLink} onChange={(e) => setSendGovLink(e.target.checked)}>
                        Send link to patient <b>email and phone</b> to fill uploads
                    </Checkbox>

                    {/* GOV text inputs ALWAYS visible + required */}
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={2}>
                        <FormControl isRequired isInvalid={idCheck.exists}>
                            <FormLabel>ID / License Number</FormLabel>
                            <Input
                                value={gov.idNumber}
                                onChange={(e) => setGov((s) => ({ ...s, idNumber: e.target.value }))}
                                onBlur={() => setGov((s) => ({ ...s, idNumber: (s.idNumber || '').trim() }))}
                            />
                            {idCheck.loading && <Text fontSize="xs" color="gray.500" mt={1}>Checking…</Text>}
                            <FormErrorMessage>
                                {idCheck.message || 'Duplicate ID number in this facility.'}
                            </FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Issuing State</FormLabel>
                            <Select
                                options={STATES}
                                value={govStateSelect}
                                onChange={(opt) => {
                                    setGovStateSelect(opt);
                                    setGov((s) => ({ ...s, issuingState: opt ? opt.value : '' }));
                                }}
                                placeholder="Select state"
                                isClearable
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Expiration</FormLabel>
                            <Input
                                type="date"
                                value={gov.expiration}
                                onChange={(e) => setGov((s) => ({ ...s, expiration: e.target.value }))}
                            />
                        </FormControl>

                        {/* GOV uploads only when NOT sending link */}
                        {!sendGovLink && (
                            <>
                                <FormControl isRequired>
                                    <FormLabel>ID Front (upload)</FormLabel>
                                    <Input type="file" accept="image/*" onChange={(e) => setGov((s) => ({ ...s, frontFile: e.target.files?.[0] || null }))} />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>ID Back (upload)</FormLabel>
                                    <Input type="file" accept="image/*" onChange={(e) => setGov((s) => ({ ...s, backFile: e.target.files?.[0] || null }))} />
                                </FormControl>

                                <HStack spacing={6} mt={2}>
                                    {idFrontPreview ? <Image src={idFrontPreview} alt="ID Front" maxH="120px" borderRadius="md" /> : <Text>Front preview: —</Text>}
                                    {idBackPreview ? <Image src={idBackPreview} alt="ID Back" maxH="120px" borderRadius="md" /> : <Text>Back preview: —</Text>}
                                </HStack>
                            </>
                        )}
                    </SimpleGrid>

                    <Divider />

                    <Heading size="md">Insurance(s)</Heading>

                    <Checkbox isChecked={sendInsLink} onChange={(e) => setSendInsLink(e.target.checked)}>
                        Send link to patient <b>email and phone</b> to fill uploads
                    </Checkbox>

                    {/* Insurance cards ALWAYS visible; uploads hidden when sending link */}
                    <>
                        {insurances.map((ins, idx) => (
                            <Box key={idx} borderWidth="1px" borderRadius="md" p={4} mb={3}>
                                <HStack justify="space-between" mb={3}>
                                    <Heading size="sm">Insurance #{idx + 1}</Heading>
                                    {insurances.length > 1 && (
                                        <IconButton aria-label="Remove" icon={<DeleteIcon />} size="sm" colorScheme="red" onClick={() => removeInsurance(idx)} />
                                    )}
                                </HStack>
                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                    <FormControl isRequired><FormLabel>Payer Name</FormLabel><Input value={ins.payerName} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, payerName: e.target.value } : x)))} /></FormControl>
                                    <FormControl isRequired><FormLabel>Plan Name</FormLabel><Input value={ins.planName} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, planName: e.target.value } : x)))} /></FormControl>
                                    <FormControl isRequired><FormLabel>Member ID</FormLabel><Input value={ins.memberId} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, memberId: e.target.value } : x)))} /></FormControl>
                                    <FormControl isRequired><FormLabel>Group No</FormLabel><Input value={ins.groupNo} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, groupNo: e.target.value } : x)))} /></FormControl>
                                    <FormControl isRequired><FormLabel>Subscriber</FormLabel><Input value={ins.subscriber} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, subscriber: e.target.value } : x)))} /></FormControl>
                                    <FormControl isRequired><FormLabel>Relationship</FormLabel><Input value={ins.relationship} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, relationship: e.target.value } : x)))} /></FormControl>
                                    <FormControl isRequired><FormLabel>Effective At</FormLabel><Input type="date" value={ins.effectiveAt} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, effectiveAt: e.target.value } : x)))} /></FormControl>

                                    {!sendInsLink && (
                                        <>
                                            <FormControl isRequired><FormLabel>Card Front (upload)</FormLabel><Input type="file" accept="image/*" onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, frontFile: e.target.files?.[0] || null } : x)))} /></FormControl>
                                            <FormControl isRequired><FormLabel>Card Back (upload)</FormLabel><Input type="file" accept="image/*" onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, backFile: e.target.files?.[0] || null } : x)))} /></FormControl>
                                        </>
                                    )}
                                </SimpleGrid>

                                {!sendInsLink && (
                                    <HStack spacing={6} mt={3}>
                                        {insPreviews[idx]?.front ? <Image src={insPreviews[idx].front} alt={`Insurance ${idx + 1} Front`} maxH="120px" borderRadius="md" /> : <Text>Front preview: —</Text>}
                                        {insPreviews[idx]?.back ? <Image src={insPreviews[idx].back} alt={`Insurance ${idx + 1} Back`} maxH="120px" borderRadius="md" /> : <Text>Back preview: —</Text>}
                                    </HStack>
                                )}
                            </Box>
                        ))}
                        <Button leftIcon={<AddIcon />} variant="outline" onClick={addInsurance}>Add Insurance</Button>
                    </>

                    <HStack justify="space-between" mt={2}>
                        <Button onClick={back}>Back</Button>
                        <Button
                            colorScheme="teal"
                            onClick={() => validateStep2() && next()}
                            isDisabled={blockNextOnDup || idCheck.loading}
                        >
                            Next
                        </Button>
                    </HStack>
                </VStack>
            )}

            {/* STEP 3: REVIEW */}
            {step === 3 && (
                <VStack spacing={6} align="stretch">
                    <Heading size="md">Review</Heading>

                    <Box borderWidth="1px" borderRadius="md" p={4}>
                        <Heading size="sm" mb={3}>Facility</Heading>
                        <Text>{facilitySelect?.label || '-'}</Text>
                    </Box>

                    <Box borderWidth="1px" borderRadius="md" p={4}>
                        <Heading size="sm" mb={3}>Identity & Contact</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                            <Text><b>Patient #:</b> {identity.patientNumber || '—'}</Text>
                            <Text><b>Name:</b> {[identity.firstName, identity.middleName, identity.lastName].filter(Boolean).join(' ')}</Text>
                            <Text><b>DOB / Age / Sex:</b> {(identity.dob || '—')} / {(age || '—')} / {(identity.sex || '—')}</Text>
                            <Text><b>Phone:</b> {digitsOnly(identity.phone) || '—'}</Text>
                            <Text><b>Email:</b> {identity.email || '—'}</Text>
                            <Text><b>Address:</b> {[identity.addressLine1, identity.addressLine2, identity.city, identity.state, identity.zip].filter(Boolean).join(', ') || '—'}</Text>
                        </SimpleGrid>
                    </Box>

                    <Box borderWidth="1px" borderRadius="md" p={4}>
                        <Heading size="sm" mb={3}>Government ID</Heading>
                        {sendGovLink && <Badge colorScheme="purple" mb={2}>Will send link to patient to upload ID/DL</Badge>}
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                            <Text><b>ID #:</b> {gov.idNumber || '—'}</Text>
                            <Text><b>Issuing State:</b> {gov.issuingState || '—'}</Text>
                            <Text><b>Expiration:</b> {gov.expiration || '—'}</Text>
                        </SimpleGrid>
                        {!sendGovLink && (
                            <HStack spacing={6}>
                                {idFrontPreview ? <Image src={idFrontPreview} alt="ID Front" maxH="140px" borderRadius="md" /> : <Text>Front: —</Text>}
                                {idBackPreview ? <Image src={idBackPreview} alt="ID Back" maxH="140px" borderRadius="md" /> : <Text>Back: —</Text>}
                            </HStack>
                        )}
                    </Box>

                    <Box borderWidth="1px" borderRadius="md" p={4}>
                        <Heading size="sm" mb={3}>Insurance</Heading>
                        {sendInsLink && <Badge colorScheme="purple" mb={2}>Will send link to patient to upload Insurance card</Badge>}
                        {insurances.map((ins, i) => (
                            <Box key={i} mb={4} borderWidth="1px" borderRadius="md" p={3}>
                                <Text mb={2}><b>Insurance #{i + 1}</b></Text>
                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                                    <Text><b>Payer:</b> {ins.payerName || '—'}</Text>
                                    <Text><b>Plan:</b> {ins.planName || '—'}</Text>
                                    <Text><b>Member ID:</b> {ins.memberId || '—'}</Text>
                                    <Text><b>Group No:</b> {ins.groupNo || '—'}</Text>
                                    <Text><b>Subscriber:</b> {ins.subscriber || '—'}</Text>
                                    <Text><b>Relationship:</b> {ins.relationship || '—'}</Text>
                                    <Text><b>Effective At:</b> {ins.effectiveAt || '—'}</Text>
                                </SimpleGrid>
                                {!sendInsLink && (
                                    <HStack spacing={6}>
                                        {insPreviews[i]?.front ? <Image src={insPreviews[i].front} alt={`Insurance ${i + 1} Front`} maxH="140px" borderRadius="md" /> : <Text>Front: —</Text>}
                                        {insPreviews[i]?.back ? <Image src={insPreviews[i].back} alt={`Insurance ${i + 1} Back`} maxH="140px" borderRadius="md" /> : <Text>Back: —</Text>}
                                    </HStack>
                                )}
                            </Box>
                        ))}
                    </Box>

                    <HStack justify="space-between" mt={2}>
                        <Button onClick={back}>Back</Button>
                        <Button colorScheme="teal" isLoading={submitting} onClick={submit} isDisabled={idCheck.loading || idCheck.exists}>
                            Create Patient
                        </Button>
                    </HStack>
                </VStack>
            )}
        </Box>
    );
};

export default PatientCreatePage;
